import { uniqBy } from 'lodash-es';
import type { CreativeEntry } from 'types/shared';
import type { CreateFacebookAdFromPacksParams, CreatePartnershipAdsParams } from '../api';
import type { CreativePayloadEntry } from '../api/types';
import type { AdSetEntry } from '../api/getAdSets';
import { SINGLE_AD_TYPE } from '../constants';
import { PartnershipCreationMode, PartnershipIdentityToShow } from '../types';
import type { FacebookFormData } from '../validation/facebookValidator';
import { toPartnershipAdSettingsFields } from '../validation/adSettingsValidation';
import type { FacebookAdSubmitContext } from './facebookAdSubmitContext';
import { countPackCreatives, filterAdSettingsWithAds } from './flowAdSetCount';

const buildCreativePacks = (
  data: FacebookFormData,
  creativesById: Map<string, CreativeEntry>,
): {
  creativePacks: CreateFacebookAdFromPacksParams['creative_packs'];
  missingCreativeIds: string[];
} => {
  const isMarketingProcessHypothesisTesting = data.marketingProcess === 'hypothesis_testing';
  const missingCreativeIds = new Set<string>();

  const creativePacks = data.packsWithCreatives.map(
    ({ creatives: items, tag, visual_touch_ups, adType: packAdType }) => ({
      visual_touch_ups,
      tag,
      ad_type: isMarketingProcessHypothesisTesting ? SINGLE_AD_TYPE : packAdType,
      creatives: items
        .map((packId) => {
          const creative = creativesById.get(String(packId)) as CreativePayloadEntry | undefined;
          if (!creative) {
            missingCreativeIds.add(String(packId));
            return null;
          }
          return {
            id: creative.id,
            creative_id: creative.creative_id,
            name: creative.name,
            creative_type: creative.creative_type,
            thumbnail_url: creative.thumbnail_url ?? null,
          };
        })
        .filter((creative): creative is CreativePayloadEntry => !!creative),
    }),
  );

  return {
    creativePacks,
    missingCreativeIds: Array.from(missingCreativeIds),
  };
};

const buildAdSetsPayload = (
  data: FacebookFormData,
  cachedAdSets: AdSetEntry[],
): AdSetEntry[] | null => {
  const isMarketingProcessInbox = Boolean(
    data.marketingProcess && data.marketingProcess.includes('inbox'),
  );

  const selectedAdSets = data.selectedAdSets
    ? uniqBy(cachedAdSets, 'id').filter((adSet) =>
      data.selectedAdSets?.find(({ id }) => id === adSet.id),
    )
    : null;

  if (!selectedAdSets) {
    return null;
  }

  return selectedAdSets.map((adSet) => {
    const campaignStatus = data.selectedCampaigns?.find(
      ({ id }) => id === adSet.campaign_id,
    )?.status;

    const status = data.selectedAdSets?.find(({ id }) => id === adSet.id)?.status;

    const budget = adSet?.is_cbo
      ? Number(data.selectedCampaignsBudgets?.[adSet.campaign_id] ?? '')
      : Number(data.selectedAdSetsBudgets?.[adSet.id] ?? '');

    const duplicatesCount = !isMarketingProcessInbox
      ? {
        duplicates_count: Number(data.selectedAdSetsDuplicatesCount?.[adSet.id]),
      }
      : undefined;

    const startTime = !isMarketingProcessInbox
      ? data.selectedAdSetsStartTimes?.[adSet.id] || undefined
      : undefined;

    return {
      ...adSet,
      ...duplicatesCount,
      ...(startTime ? { start_time: startTime } : {}),
      budget,
      status,
      campaign_status: campaignStatus,
    };
  }) as AdSetEntry[];
};

export const getMissingCreativeIds = (
  data: FacebookFormData,
  context: FacebookAdSubmitContext,
): string[] => buildCreativePacks(data, context.creativesById).missingCreativeIds;

const buildSharedSubmitFields = (
  data: FacebookFormData,
  context: FacebookAdSubmitContext,
) => {
  const { creativePacks } = buildCreativePacks(
    data,
    context.creativesById,
  );

  return {
    app: data.app,
    platform: data.platform,
    call_to_action: data.callToActions,
    creative_packs: creativePacks,
    ad_sets: buildAdSetsPayload(data, context.cachedAdSets),
    hotjar: data.hotjar,
    conversion_domain: data.selectedConversionDomain,
    optional_ad_label: data.adLabelSuffix,
  };
};

export const buildCreateAdFromPacksPayload = (
  data: FacebookFormData,
  context: FacebookAdSubmitContext,
): CreateFacebookAdFromPacksParams => {
  const sharedFields = buildSharedSubmitFields(data, context);
  const hypothesis =
    data.marketingProcess === 'hypothesis_testing'
      ? data.packsWithCreatives.find((pack) => pack.tag?.trim())?.tag
      : undefined;
  const selectedAdSetsWithNames = sharedFields.ad_sets ?? [];

  return {
    ...sharedFields,
    conversion_domain: sharedFields.conversion_domain?.trim()
      ? sharedFields.conversion_domain
      : null,
    visual_touch_ups: data.packsWithCreatives.some((pack) => pack.visual_touch_ups),
    scaling_flows: null,
    ...(hypothesis ? { hypothesis } : {}),
    creative_packs: sharedFields.creative_packs.map((pack, index) => {
      const formPack = data.packsWithCreatives[index];
      const creativesCount = formPack ? countPackCreatives(formPack) : pack.creatives.length;

      return {
        ...pack,
        ad_settings: filterAdSettingsWithAds(
          data.selectedAdSettings[index],
          selectedAdSetsWithNames,
          creativesCount,
        ),
      };
    }),
  };
};

export const buildCreatePartnershipAdsPayload = (
  data: FacebookFormData,
  context: FacebookAdSubmitContext,
): CreatePartnershipAdsParams => {
  const sharedFields = buildSharedSubmitFields(data, context);
  const hypothesis =
    data.marketingProcess === 'hypothesis_testing'
      ? data.packsWithCreatives.find((pack) => pack.tag?.trim())?.tag
      : undefined;

  return {
    ...sharedFields,
    conversion_domain: sharedFields.conversion_domain?.trim()
      ? sharedFields.conversion_domain
      : null,
    visual_touch_ups: data.packsWithCreatives.some((pack) => pack.visual_touch_ups),
    scaling_flows: null,
    ...(hypothesis ? { hypothesis } : {}),
    partnership_ad_settings: data.partnershipAdSettings
      .map((setting, index) => ({ setting, index }))
      .filter(({ setting }) => Boolean(setting.partnershipId?.trim()))
      .map(({ setting, index }) => ({
        partner_page_id: setting.partnershipPartnerPageId ?? null,
        partner_instagram_id: setting.partnershipPartnerInstagramId ?? null,
        is_partner_identity_first: data.partnershipIsPartnerIdentityFirst ?? true,
        identity_to_show: data.partnershipIdentityToShow ?? PartnershipIdentityToShow.BOTH,
        creation_mode: data.partnershipCreationMode ?? PartnershipCreationMode.PARTNERSHIP_ONLY,
        tag: setting.partnershipTag ?? '',
        ad_settings: toPartnershipAdSettingsFields(data.selectedAdSettings[index]),
      })),
  };
};
